import PropTypes from "prop-types";
import classNames from "classnames";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdPhone } from "react-icons/md";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import Tooltip from "shared/components/Tooltip";
import Spin from "shared/components/Spin";
import queries from "utils/api/graphql/queries";
import mutations from "utils/api/graphql/mutations";

const STATE_DEFAULT = "default";
const STATE_LOADING = "loading";
const STATE_SUCCESS = "success";
const STATE_ERROR = "error";
const SUCCESS_DURATION_MS = 3000;
const ERROR_DURATION_MS = 5000;

const CallButton = ({
  phoneNumber,
  contactId,
  projectId,
  getPopupContainer,
  className,
  buttonClassName,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const DRAWER = t("DRAWER", { returnObjects: true });
  const [uiState, setUiState] = useState(STATE_DEFAULT);
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: callStatusData } = useQuery(queries.CALL_STATUS, {
    skip: !phoneNumber,
  });
  const [callDial, { loading: dialLoading }] = useMutation(
    mutations.CALL_DIAL,
    {
      onCompleted: (result) => {
        if (get(result, "callDial.success")) {
          setUiState(STATE_SUCCESS);
          setErrorMessage(null);
        } else {
          setUiState(STATE_ERROR);
          setErrorMessage(get(result, "callDial.message") || null);
        }
      },
      onError: (err) => {
        setUiState(STATE_ERROR);
        setErrorMessage(err?.message || DRAWER.CALL?.ERROR_GENERIC);
      },
    },
  );

  const callStatus = get(callStatusData, "callStatus");
  const hasAircallMapping = get(callStatus, "hasAircallMapping") || true;

  useEffect(() => {
    if (dialLoading) setUiState(STATE_LOADING);
  }, [dialLoading]);

  useEffect(() => {
    if (uiState !== STATE_SUCCESS && uiState !== STATE_ERROR) return;
    const duration =
      uiState === STATE_SUCCESS ? SUCCESS_DURATION_MS : ERROR_DURATION_MS;
    const timer = setTimeout(() => {
      setUiState(STATE_DEFAULT);
      setErrorMessage(null);
    }, duration);
    return () => clearTimeout(timer);
  }, [uiState]);

  const isDisabled =
    !hasAircallMapping || uiState === STATE_LOADING || !contactId || !projectId;
  const isDisabledWithTooltip = !hasAircallMapping;
  const tooltipTitle = isDisabledWithTooltip
    ? DRAWER.CALL?.TOOLTIP_NOT_CONFIGURED
    : uiState === STATE_SUCCESS
      ? DRAWER.CALL?.SUCCESS_MESSAGE
      : uiState === STATE_ERROR
        ? errorMessage
        : DRAWER.CALL?.TOOLTIP;

  const handleClick = () => {
    if (isDisabled || uiState === STATE_SUCCESS) return;
    callDial({
      variables: { phoneNumber, contactId, projectId },
    });
  };

  const content =
    uiState === STATE_LOADING ? <Spin size="small" /> : <MdPhone />;

  return (
    <Tooltip title={tooltipTitle} getPopupContainer={getPopupContainer}>
      <div
        role="button"
        tabIndex={0}
        className={classNames("cta-bar__btn", "call-button", buttonClassName, {
          "cta-bar__btn--active": uiState === STATE_SUCCESS,
          "cta-bar__btn--disabled": isDisabled,
        })}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
        aria-label={DRAWER.CALL?.TOOLTIP}
      >
        {content}
      </div>
    </Tooltip>
  );
};

CallButton.propTypes = {
  phoneNumber: PropTypes.string,
  contactId: PropTypes.string,
  projectId: PropTypes.string,
  getPopupContainer: PropTypes.func,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
};

export default CallButton;
